import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { isHttpStatus } from "@workspace/base-api/fetchJson";

import { eposApi } from "../../api";
import type { Order } from "../../types";
import { optionRequiredOf, type OptionRequired } from "../option-required";
import type { SendWarning } from "../send-warnings";

interface UseCourseFireOptions {
	tenant: string;
	venueId: string;
	deviceId: string | null;
	currentOrder: Order | null;
	onUpdateOrder: (order: Order) => void;
	/**
	 * HA-619 — firing a course is a send, so it warns like one. The hook owns no UI, so the screen
	 * decides between the blocking modal and the banner.
	 */
	onSendWarnings?: (warnings: SendWarning[]) => void;
	/**
	 * HA-1024 — the commit was refused because a fired line has no choice for a required option group. The screen
	 * opens that line's editor with the message inside it, as for every other option refusal.
	 */
	onOptionRefusal?: (orderId: string, missing: OptionRequired) => void;
}

interface PendingFireState {
	/** HA-803: the order this fire is on. The operator can switch orders during the countdown. */
	orderId: string;
	courseId: string;
	countdown: number;
}

export function useCourseFire({
	tenant,
	venueId,
	deviceId,
	currentOrder,
	onUpdateOrder,
	onSendWarnings,
	onOptionRefusal,
}: UseCourseFireOptions) {
	const [pendingFire, setPendingFire] = useState<PendingFireState | null>(null);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const currentOrderRef = useRef(currentOrder);
	useEffect(() => {
		currentOrderRef.current = currentOrder;
	}, [currentOrder]);

	const clearTimer = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	};

	const queryClient = useQueryClient();

	/**
	 * HA-768 / D-HA-24 — the order as the server has it now, merged in. A refused fire, commit or
	 * cancel means this terminal's idea of the fire is out of date, so it is replaced rather than
	 * reported as an error the operator can do nothing about.
	 */
	const refreshOrder = async (): Promise<Order | undefined> => {
		const orderId = currentOrderRef.current?.id;
		if (!orderId || !navigator.onLine) return undefined;

		try {
			// One quick look: React Query's defaults would retry it three times, and pause it offline.
			const epos = await queryClient.fetchQuery({
				...eposApi.getEposOptions(tenant, venueId),
				staleTime: 0,
				retry: false,
				networkMode: "always",
			});
			const server = epos.orders.find((o) => o.id === orderId);
			if (server) mergeOrderResponse(server);
			return server;
		} catch {
			return undefined;
		}
	};

	/**
	 * HA-768 / D-HA-24 — the server refused a fire (409): one is already pending. Recover to it: if
	 * it is this terminal's (a reload lost the countdown), the Undo/Confirm controls come back, with
	 * a fresh undo window, since the operator has just asked to fire; if another terminal's, the
	 * screen shows that (otherDevicePendingFire) instead of an error. It used to stay stuck on a
	 * "Failed to fire course" with nothing to press.
	 */
	const recoverPendingFire = async () => {
		const server = await refreshOrder();
		const pending = server?.pendingCourseFire;

		if (!pending) {
			toast.error("Failed to fire course", {
				description: "The course fire could not be started. Try again.",
			});
			return;
		}

		if (pending.deviceId === deviceId) {
			setPendingFire({
				orderId: server.id,
				courseId: pending.courseId,
				countdown: 5,
			});
			return;
		}

		toast.warning("Another terminal is firing a course on this order");
	};

	/**
	 * HA-768 — a commit or cancel refused because there is no pending fire any more (400): it was
	 * settled already, here or elsewhere. The server's state is shown; it is an error only if the
	 * fire is somehow still pending.
	 */
	const settledElsewhere = async (error: unknown, title: string) => {
		if (isHttpStatus(error, 400)) {
			const server = await refreshOrder();
			if (server && !server.pendingCourseFire) return;
		}

		toast.error(title, {
			description: error instanceof Error ? error.message : "Unknown error",
		});
	};

	const mergeOrderResponse = (response: Order) => {
		const order = currentOrderRef.current;
		// HA-803: a fire's answer can arrive after the operator switched orders; it is merged only
		// into the order it is for, never over the one now on screen.
		if (!order || order.id !== response.id) return;
		onUpdateOrder({
			...order,
			...response,
			items: [
				...order.items.filter(
					(x) => !response.items.some((i) => i.id === x.id),
				),
				...response.items,
			],
		});
	};

	const { mutateAsync: initiateFireAsync, isPending: isInitiating } =
		useMutation({
			...eposApi.initiateCourseFireOptions(tenant, venueId),
			onSuccess(d) {
				if (!d.success) return;
				mergeOrderResponse(d.response);
			},
			onError(error) {
				if (isHttpStatus(error, 409)) {
					void recoverPendingFire();
					return;
				}
				toast.error("Failed to fire course", {
					description: error instanceof Error ? error.message : "Unknown error",
				});
			},
		});

	// HA-803: a commit or cancel settles only the fire on ITS order; by the time it answers, the
	// pending fire may be a newer one on another order.
	const settleFire = (orderId: string) =>
		setPendingFire((prev) => (prev?.orderId === orderId ? null : prev));

	const {
		mutate: commitFire,
		mutateAsync: commitFireAsync,
		isPending: isCommitting,
	} = useMutation({
		...eposApi.commitCourseFireOptions(tenant, venueId),
		onSuccess(d, { orderId }) {
			if (!d.success) return;
			mergeOrderResponse(d.response.order);
			settleFire(orderId);

			if (d.response.warnings.length > 0) onSendWarnings?.(d.response.warnings);
		},
		onError(error, { orderId }) {
			settleFire(orderId);
			const missing = optionRequiredOf(error);
			if (missing && onOptionRefusal) {
				onOptionRefusal(orderId, missing);
				return;
			}
			void settledElsewhere(error, "Failed to commit course fire");
		},
	});

	const { mutateAsync: cancelFireAsync } = useMutation({
		...eposApi.cancelCourseFireOptions(tenant, venueId),
		onSuccess(d, { orderId }) {
			if (!d.success) return;
			mergeOrderResponse(d.response);
			settleFire(orderId);
		},
		onError(error, { orderId }) {
			settleFire(orderId);
			void settledElsewhere(error, "Failed to cancel course fire");
		},
	});

	// Countdown timer — ticks every second while a fire is pending. HA-781: one interval per fire
	// (keyed on the fire's course, not on the state it ticks), and the updater only counts down:
	// React may call an updater more than once (StrictMode, replays), so the commit lived in one
	// could send a course to the kitchen twice.
	const pendingFireKey = pendingFire
		? `${pendingFire.orderId}:${pendingFire.courseId}`
		: null;
	useEffect(() => {
		if (pendingFireKey === null) return;

		const id = setInterval(() => {
			setPendingFire((prev) =>
				prev && prev.countdown > 0
					? { ...prev, countdown: prev.countdown - 1 }
					: prev,
			);
		}, 1000);
		timerRef.current = id;

		return () => {
			clearInterval(id);
			if (timerRef.current === id) timerRef.current = null;
		};
	}, [pendingFireKey]);

	// ...and it auto-commits at 0, here, once: the ref remembers the fire it committed, so a re-run
	// of this effect can't commit the same one again.
	const committedFireRef = useRef<PendingFireState | null>(null);
	useEffect(() => {
		if (!pendingFire || pendingFire.countdown > 0) return;
		if (committedFireRef.current === pendingFire) return;

		committedFireRef.current = pendingFire;
		clearTimer();
		setPendingFire(null);
		// HA-803: the fire's own order, not whichever is on screen now.
		commitFire({
			orderId: pendingFire.orderId,
			deviceId: deviceId ?? undefined,
		});
	}, [pendingFire, commitFire, deviceId]);

	// Offline handling — commit on reconnect
	const pendingFireRef = useRef(pendingFire);
	useEffect(() => {
		pendingFireRef.current = pendingFire;
	}, [pendingFire]);

	useEffect(() => {
		const handleOnline = () => {
			const pending = pendingFireRef.current;
			if (pending) {
				// HA-530 AC13 — the fire commits the moment connectivity returns, so the undo window is
				// over: stop the countdown before committing. HA-768: left running, it reached zero while
				// a slow commit was still in flight and committed a second time, and the server's refusal
				// of that duplicate showed "Failed to commit course fire" for a fire that had gone through.
				clearTimer();
				setPendingFire(null);
				commitFire({
					orderId: pending.orderId,
					deviceId: deviceId ?? undefined,
				});
			}
		};

		window.addEventListener("online", handleOnline);
		return () => window.removeEventListener("online", handleOnline);
	}, [commitFire, deviceId]);

	const fireNextCourse = async () => {
		if (!currentOrder) return;

		// HA-803: one pending fire per terminal. A fire still counting down on another order is
		// committed now rather than stranded: its undo window ends when a new fire starts.
		const other = pendingFireRef.current;
		if (other && other.orderId !== currentOrder.id) {
			committedFireRef.current = other;
			clearTimer();
			setPendingFire(null);
			commitFire({ orderId: other.orderId, deviceId: deviceId ?? undefined });
		}

		// The mutation's onError reports or recovers a refusal; nothing is left for the caller.
		const result = await initiateFireAsync({
			orderId: currentOrder.id,
			deviceId: deviceId ?? undefined,
		}).catch(() => null);

		if (result?.success && result.response.pendingCourseFire) {
			setPendingFire({
				orderId: currentOrder.id,
				courseId: result.response.pendingCourseFire.courseId,
				countdown: 5,
			});
		}
	};

	// HA-803: Undo and Confirm act on the fire's own order (the one they are shown for); with no
	// local fire (a reload lost the countdown) they fall back to the order on screen, as before.
	const undoFire = async () => {
		const orderId = pendingFireRef.current?.orderId ?? currentOrder?.id;
		if (!orderId) return;
		clearTimer();
		setPendingFire(null);
		await cancelFireAsync({ orderId }).catch(() => {});
	};

	const confirmFire = async () => {
		const pending = pendingFireRef.current;
		const orderId = pending?.orderId ?? currentOrder?.id;
		if (!orderId) return;
		if (pending) committedFireRef.current = pending;
		clearTimer();
		setPendingFire(null);
		await commitFireAsync({
			orderId,
			deviceId: deviceId ?? undefined,
		}).catch(() => {});
	};

	// Determine if another device has a pending fire
	const otherDevicePendingFire =
		currentOrder?.pendingCourseFire != null &&
		currentOrder.pendingCourseFire.deviceId !== deviceId;

	// Determine if there are unfired standard courses with items
	const hasUnfiredCourses =
		currentOrder != null &&
		currentOrder.items.some(
			(i) =>
				i.courseId != null &&
				!i.sentToKitchen &&
				!(currentOrder.firedCourseIds ?? []).includes(i.courseId),
		);

	return {
		// HA-803: another order's countdown is not shown on this one (it keeps running and commits).
		pendingFire:
			pendingFire && pendingFire.orderId === currentOrder?.id
				? pendingFire
				: null,
		fireNextCourse,
		undoFire,
		confirmFire,
		isInitiating,
		isCommitting,
		otherDevicePendingFire,
		hasUnfiredCourses,
	};
}
